import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiCheck, FiSearch, FiX } from '../../../icons';
import './SearchableSelect.scss';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  className?: string;
  loading?: boolean;
  loadingText?: string;
  searchPlaceholder?: string;
  noOptionsText?: string;
  clearable?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value = '',
  placeholder = 'Select an option',
  onChange,
  disabled = false,
  isInvalid = false,
  className = '',
  loading = false,
  loadingText = 'Loading...',
  searchPlaceholder = 'Search...',
  noOptionsText = 'No options found',
  clearable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset focused index when filtered options change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredOptions]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || loading) return;

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          const selectedOpt = filteredOptions[focusedIndex];
          if (selectedOpt && !selectedOpt.disabled) {
            onChange(selectedOpt.value);
            setIsOpen(false);
            setSearchTerm('');
            setFocusedIndex(-1);
          }
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex(prev => {
            let nextIndex = prev + 1;
            while (nextIndex < filteredOptions.length && filteredOptions[nextIndex].disabled) {
              nextIndex++;
            }
            return nextIndex < filteredOptions.length ? nextIndex : prev;
          });
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => {
            let nextIndex = prev - 1;
            while (nextIndex >= 0 && filteredOptions[nextIndex].disabled) {
              nextIndex--;
            }
            return nextIndex >= 0 ? nextIndex : prev;
          });
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
    }
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
      const selectedIndex = filteredOptions.findIndex(option => option.value === value);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  };

  const handleOptionClick = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
    setFocusedIndex(-1);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange('');
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setFocusedIndex(0);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow normal input behavior for search
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === 'Escape') {
      handleKeyDown(event);
    }
  };

  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current && filteredOptions.length > 0) {
      const focusedElement = optionsRef.current.children[focusedIndex + 1] as HTMLElement; // +1 for search input
      if (focusedElement) {
        focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [focusedIndex, isOpen, filteredOptions.length]);

  return (
    <div
      ref={selectRef}
      className={`searchable-select ${className} ${isInvalid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
    >
      <motion.div
        className={`select-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        whileHover={!disabled && !loading ? { 
          boxShadow: '0 4px 15px rgba(57, 87, 160, 0.15)' 
        } : undefined}
        whileTap={!disabled && !loading ? { scale: 0.98 } : undefined}
      >
        <span className="select-value">
          {loading ? loadingText : (selectedOption ? selectedOption.label : placeholder)}
        </span>
        <div className="select-actions">
          {clearable && value && !loading && (
            <motion.button
              type="button"
              className="clear-button"
              onClick={handleClear}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </motion.button>
          )}
          <motion.div
            className="select-arrow"
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronDown />
          </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {isOpen && !loading && (
          <motion.div
            ref={optionsRef}
            className="select-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {/* Search Input */}
            <div className="search-container">
              <FiSearch className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </div>

            {/* Options */}
            {filteredOptions.length === 0 ? (
              <div className="select-option no-options">{noOptionsText}</div>
            ) : (
              filteredOptions.map((option, index) => (
                <motion.div
                  key={option.value}
                  className={`select-option ${option.disabled ? 'disabled' : ''} ${
                    focusedIndex === index ? 'focused' : ''
                  } ${value === option.value ? 'selected' : ''}`}
                  onClick={() => handleOptionClick(option)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.1, delay: index * 0.02 }}
                >
                  <span className="option-text">{option.label}</span>
                  {value === option.value && (
                    <motion.div
                      className="option-check"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FiCheck />
                    </motion.div>
                  )}
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchableSelect;